import { useEffect, useMemo, useRef, useState } from "react";
import {
  PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_INTERVAL_MS,
  createParticipantLayoutAudioActivityMonitor,
  disposeParticipantLayoutAudioActivityMonitor,
  getParticipantLayoutDialogAudioTracksByOwnerId,
  sampleParticipantLayoutAudioActivityMonitor
} from "./participant-layout-dialog-audio-activity";

function getAppObject() {
  if (typeof window !== "undefined") {
    return window.APP;
  }

  if (typeof global !== "undefined") {
    return global.APP;
  }

  return undefined;
}

function getCurrentTime() {
  if (typeof performance !== "undefined" && performance.now) {
    return performance.now();
  }

  return Date.now();
}

function areSpeakingStatesEqual(currentState, nextState) {
  const currentKeys = Object.keys(currentState);
  const nextKeys = Object.keys(nextState);

  if (currentKeys.length !== nextKeys.length) {
    return false;
  }

  return nextKeys.every(key => currentState[key] === nextState[key]);
}

function disposeParticipantLayoutAudioActivityMonitors(monitors) {
  monitors.forEach(disposeParticipantLayoutAudioActivityMonitor);
  monitors.clear();
}

function getParticipantIdsKey(participantIds) {
  if (!Array.isArray(participantIds)) {
    return null;
  }

  return participantIds.filter(Boolean).join("\u0000");
}

function getParticipantIdSetFromKey(participantIdsKey) {
  if (participantIdsKey === null) {
    return null;
  }

  if (!participantIdsKey) {
    return new Set();
  }

  return new Set(participantIdsKey.split("\u0000"));
}

export function useParticipantLayoutDialogAudioActivityState({ enabled = true, mySessionId, participantIds } = {}) {
  const app = getAppObject();
  const monitorsRef = useRef(new Map());
  const [speakingBySessionId, setSpeakingBySessionId] = useState({});
  const participantIdsKey = useMemo(() => getParticipantIdsKey(participantIds), [participantIds]);

  useEffect(() => {
    const monitors = monitorsRef.current;
    const participantIdSet = getParticipantIdSetFromKey(participantIdsKey);

    const setSpeakingState = nextSpeakingBySessionId => {
      setSpeakingBySessionId(currentSpeakingBySessionId => {
        return areSpeakingStatesEqual(currentSpeakingBySessionId, nextSpeakingBySessionId)
          ? currentSpeakingBySessionId
          : nextSpeakingBySessionId;
      });
    };

    const syncMonitors = () => {
      const audioTracksByOwnerId = getParticipantLayoutDialogAudioTracksByOwnerId({
        dialog: app?.dialog,
        mySessionId,
        ownerIds: participantIdSet
      });
      const ownerIds = new Set(Object.keys(audioTracksByOwnerId));

      monitors.forEach((monitor, ownerId) => {
        if (!ownerIds.has(ownerId) || monitor.track !== audioTracksByOwnerId[ownerId]) {
          disposeParticipantLayoutAudioActivityMonitor(monitor);
          monitors.delete(ownerId);
        }
      });

      Object.entries(audioTracksByOwnerId).forEach(([ownerId, track]) => {
        if (monitors.has(ownerId)) {
          return;
        }

        const monitor = createParticipantLayoutAudioActivityMonitor(track);

        if (monitor) {
          monitors.set(ownerId, monitor);
        }
      });
    };

    const sampleAudioActivity = () => {
      if (!enabled) {
        disposeParticipantLayoutAudioActivityMonitors(monitors);
        setSpeakingState({});
        return;
      }

      syncMonitors();

      const now = getCurrentTime();
      const nextSpeakingBySessionId = {};

      monitors.forEach((monitor, ownerId) => {
        nextSpeakingBySessionId[ownerId] = sampleParticipantLayoutAudioActivityMonitor(monitor, now);
      });

      setSpeakingState(nextSpeakingBySessionId);
    };

    sampleAudioActivity();

    if (!enabled) {
      return () => {};
    }

    app?.dialog?.on?.("stream_updated", sampleAudioActivity);
    app?.dialog?.on?.("mic-state-changed", sampleAudioActivity);

    const intervalId = setInterval(sampleAudioActivity, PARTICIPANT_LAYOUT_AUDIO_ACTIVITY_INTERVAL_MS);

    return () => {
      app?.dialog?.off?.("stream_updated", sampleAudioActivity);
      app?.dialog?.off?.("mic-state-changed", sampleAudioActivity);
      clearInterval(intervalId);
      disposeParticipantLayoutAudioActivityMonitors(monitors);
    };
  }, [app?.dialog, enabled, mySessionId, participantIdsKey]);

  return useMemo(
    () => ({
      speakingBySessionId
    }),
    [speakingBySessionId]
  );
}
